// src/pages/Workflows/components/ExecutarModal/useExecutarWorkflow.js
// Hook: inicia execução de workflow (pre-flight → executar → polling)

import { useState, useCallback, useRef } from 'react';
import axios from 'axios';

/**
 * Dado um workflow com definicao, retorna os nós que precisam de input em runtime.
 * - formulario: pede campos configurados
 * - upload_ficheiros: pede ficheiros
 */
function detetarNosComInput(workflow) {
  const definicao = workflow?.definicao || {};
  const nodes = definicao.nodes || [];

  return nodes.filter(n => {
    const tipo = n.data?.nodeType;
    return tipo === 'formulario' || tipo === 'upload_ficheiros';
  });
}

export function useExecutarWorkflow(workflow) {
  const [etapa, setEtapa] = useState('formulario'); // 'formulario' | 'executando' | 'concluido'
  const [formValues, setFormValues] = useState({});   // { no_id: { campo: valor } }
  const [files, setFiles]           = useState({});   // { no_id: FileList }
  const [logNos, setLogNos]         = useState([]);   // [{ no_id, no_tipo, estado, output_preview, erro, ordem }]
  const [estadoFinal, setEstadoFinal] = useState(null);
  const [erro, setErro]               = useState('');
  const [execucaoId, setExecucaoId]   = useState(null);

  const pollingRef = useRef(null);

  const nosComInput = detetarNosComInput(workflow);

  // Atualiza valor de campo de formulário para um nó
  const setFormCampo = useCallback((noId, campo, valor) => {
    setFormValues(prev => ({
      ...prev,
      [noId]: { ...(prev[noId] || {}), [campo]: valor },
    }));
  }, []);

  // Atualiza ficheiros para um nó upload_ficheiros
  const setFicheirosNo = useCallback((noId, fileList) => {
    setFiles(prev => ({ ...prev, [noId]: fileList }));
  }, []);

  // ------------------------------------------------------------------
  // Polling (substitui SSE)
  // ------------------------------------------------------------------
  const iniciarPolling = useCallback((eid) => {
    const TERMINAIS = ['completed', 'failed', 'cancelled'];
    const token = localStorage.getItem('token');

    pollingRef.current = setInterval(async () => {
      try {
        const r = await axios.get(`/api/workflows/execucoes/${eid}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const { estado, nos } = r.data.data;

        setLogNos((nos || []).map(n => ({
          no_id:          n.no_id,
          no_tipo:        n.no_tipo,
          estado:         n.estado,
          output_preview: n.output
            ? JSON.stringify(n.output).substring(0, 200)
            : null,
          erro:  n.erro,
          ordem: n.ordem_execucao,
        })));

        if (TERMINAIS.includes(estado)) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
          setEstadoFinal(estado);
          setEtapa('concluido');
        }
      } catch (err) {
        console.error('Polling erro:', err);
      }
    }, 3000);
  }, []);

  // ------------------------------------------------------------------
  // Início da execução (3 fases)
  // ------------------------------------------------------------------
  const iniciarExecucao = useCallback(async () => {
    if (!workflow?.id) return;

    try {
      setErro('');
      setEtapa('executando');
      setLogNos([]);

      const token = localStorage.getItem('token');
      let eid = null;
      const preflightData = {};

      // ── FASE 1: Upload de ficheiros (nó upload_ficheiros) ──────────
      const noUpload = nosComInput.find(n => n.data?.nodeType === 'upload_ficheiros');
      if (noUpload && files[noUpload.id] && files[noUpload.id].length > 0) {
        const formData = new FormData();
        Array.from(files[noUpload.id]).forEach(f => formData.append('files', f));

        const uploadRes = await axios.post('/api/workflows/upload-temp', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization:  `Bearer ${token}`,
          },
        });

        eid = uploadRes.data.session_id;
        // Array directo — compatível com o output do uploadFicheiroHandler
        preflightData[noUpload.id] = uploadRes.data.ficheiros;
      }

      // ── FASE 2: Dados do formulário (nó formulario) ────────────────
      const noFormulario = nosComInput.find(n => n.data?.nodeType === 'formulario');
      if (noFormulario) {
        preflightData[noFormulario.id] = formValues[noFormulario.id] || {};
      }

      // ── FASE 3: Disparar execução (JSON, não multipart) ────────────
      const res = await axios.post(
        `/api/workflows/${workflow.id}/executar`,
        { execucao_id: eid, preflight_data: preflightData },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const execId = res.data.execucao_id;
      setExecucaoId(execId);

      // ── FASE 4: Polling ────────────────────────────────────────────
      iniciarPolling(execId);

    } catch (err) {
      console.error('Erro na execução:', err);
      setErro(err.response?.data?.message || err.message || 'Erro desconhecido');
      setEtapa('concluido');
    }
  }, [workflow, formValues, files, nosComInput, iniciarPolling]);

  // ------------------------------------------------------------------
  // Reset
  // ------------------------------------------------------------------
  const resetar = useCallback(() => {
    setEtapa('formulario');
    setFormValues({});
    setFiles({});
    setLogNos([]);
    setEstadoFinal(null);
    setErro('');
    setExecucaoId(null);
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  return {
    etapa,
    nosComInput,
    formValues,
    files,
    logNos,
    estadoFinal,
    erro,
    execucaoId,
    setFormCampo,
    setFicheirosNo,
    iniciarExecucao,
    resetar,
  };
}
